import React, {useEffect, useState} from 'react';
import Page from "../Components/Page";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import EditableReactTableV2 from "../Components/ReactTable/EditableReactTableV2";
import {makeApiCall, makeApiCallPost} from "../../Api/makeApiCall";
import {makeApolloClientCall} from "../../Api/makeApolloApiCall";
import {INDEX_FIELD} from "../Components/ReactTable/common_custom_columns";
import {EditFormDropdownInputField} from "../Components/ReactTable/EditRecordForm";
import {useServiceBase} from "../Components/ReactTable/useServiceBase";
import FileLink from "../IngestionLogs/Columns/FileLink";
import moment from "moment";
import {
    buildCleanseFillExtractCheckpointOptions,
    getCheckpointListDirectoriesUrl,
} from "../../utils/cleanseFillExtractCheckpoints";
import {invokeDAGviaApiPost} from "../../Api/invokeDag";
import ButtonLoader from "../../utils/components/ButtonLoader";

const PeriodReleaseCheckpoints = () => {
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [error, setError] = useState(false);
    const today = moment(Date.now()).format('yyyy-MM-DD');

    const [marketPeriodOptions, setMarketPeriodOptions] = useState([]);
    const [cleanseFillExtractTablesOptions, setCleanseFillExtractTablesOptions] = useState([]);

    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/defaultPeriodCheckPoints/`;
    const endpointPostUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/sendPeriodReleaseCheckpointPubsub/`;
    const {data, loading, onRowDeleteConfirmed, onRowEditConfirmed} = useServiceBase({endpointUrl});

    const DAG_TO_TRIGGER = "copy_prod_checkpoint"

    useEffect(() => {
        setOptionsLoading(true)
        Promise.all([
            makeApiCall(getCheckpointListDirectoriesUrl()),
            makeApolloClientCall(`query { periods(periodEndWeeksOnly: true) { periodAbbreviation endDate } }`)
        ])
            .then(([
                       cleanseFillExtractBuckets,
                       periodListResults
                   ]) => {

                const cleanseFillExtractValues = buildCleanseFillExtractCheckpointOptions(cleanseFillExtractBuckets);

                const filteredMappedPeriods = periodListResults.periods
                    .filter(period => period.endDate <= today)
                    .sort()
                    .map(period => ({value: period.periodAbbreviation, label: period.periodAbbreviation}));

                setCleanseFillExtractTablesOptions(cleanseFillExtractValues);
                setMarketPeriodOptions(filteredMappedPeriods);
            }).catch(() => setError(true))
            .finally(() => setOptionsLoading(false));
    }, []);

    const getGcsFileLocation = checkpoint => `spins-edp-${process.env.REACT_APP_ENV}-checkpoint/p_dataset=cleanse_fill_extract/p_run_id=${checkpoint}/`;

    const columns = [
        {header: "Period", accessorKey: "periodAbbreviation"},
        {header: "Checkpoint", accessorKey: "checkpoint"},
        {
            header: "Link", id: "linkToCheckpoint",
            cell: ({row}) => {
                const {checkpoint} = row.original;
                const gcsFileLocation = getGcsFileLocation(checkpoint);
                const exists = Boolean(cleanseFillExtractTablesOptions.find(x => x.value === checkpoint))
                return exists ?
                    FileLink({gcsFileLocation, displayText: "Link"}) :
               <div>
                   {process.env.REACT_APP_ENV !== 'prod' ?
                   <ButtonLoader
                       buttonTitle={"Trigger Copy Prod Checkpoint"}
                       buttonHasModal
                       modalHeader={`Checkpoint: ${checkpoint}`}
                       buttonClassName={"button primary align-bottom"}
                       modalText={`Are you sure you want to trigger ${DAG_TO_TRIGGER}?`}
                       axiosFunction={() => invokeDAGviaApiPost(DAG_TO_TRIGGER, {
                           'checkpoint_name': checkpoint
                       })}
                       toastTextFailure={`${DAG_TO_TRIGGER} DAG trigger failed with error`}
                       toastTextSuccess={`${DAG_TO_TRIGGER} DAG triggered successfully`}
                   /> :
                       <div>No link available</div>
                   }
               </div>
            }
        }
    ]

    const validatePreSave = ({newData, rows, form}) => {
        let hasDuplicate = false;
        rows.forEach(row => {
            if (row.original.periodAbbreviation === newData.periodAbbreviation && form.getValues('index') !== row.original.index) {
                hasDuplicate = true;
            }
        });

        if (hasDuplicate) {
            form.setError('periodAbbreviation', {type: 'custom', message: ['Duplicate entry detected']});
        }

        return !hasDuplicate;
    }

    async function saveDataAndSendPubsub(newOrUpdatedRow) {
        const pubsubAttributes = {
            fileLocation: `gs://${getGcsFileLocation(newOrUpdatedRow.checkpoint)}`,
            period: newOrUpdatedRow.periodAbbreviation
        };

        return onRowEditConfirmed(newOrUpdatedRow).then(async () => {
            await makeApiCallPost(endpointPostUrl, pubsubAttributes);
        });
    }

    return <Page name={'Period Release Checkpoints'}>
        {error && <div className={'error-message'} data-testid={'error-message'}>There was an error loading the data.
                Please try again later.</div>}
        {!error && <LoadingWrapper loading={loading || optionsLoading}>
            <EditableReactTableV2
                filterable={true}
                columns={columns}
                data={data.data}
                editTable={[
                    INDEX_FIELD,
                    {id: 'periodAbbreviation', Component: args => <EditFormDropdownInputField {...args} options={marketPeriodOptions} isRequired/>},
                    {id: 'checkpoint', Component: args => <EditFormDropdownInputField {...args} options={cleanseFillExtractTablesOptions} isRequired/>},
                ]}
                onRowEditConfirmed={saveDataAndSendPubsub}
                onRowDeleteConfirmed={onRowDeleteConfirmed}
                validatePreSave={validatePreSave}
                initialState={{pagination: {pageSize: 10} , sorting: [{id: "periodAbbreviation", desc: true}]}}
            />
        </LoadingWrapper>}

    </Page>
};

export default PeriodReleaseCheckpoints;